import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { ThemeProvider } from "./components/ThemeContext";
import { ThemeToggle } from "./components/ThemeToggle";
import { BlogSection } from "./components/BlogSection";
import { TestimonialsSection } from "./components/TestimonialsSection";
import { CaseStudiesSection } from "./components/CaseStudiesSection";
import { NewsletterSection } from "./components/NewsletterSection";
import { ContactForm } from "./components/ContactForm";
import PropTypes from "prop-types";
import { useState } from "react";

// Case Study Pages
import { CloudMigrationCaseStudy } from "./components/case-studies/CloudMigrationCaseStudy";
import { AICustomerServiceCaseStudy } from "./components/case-studies/AICustomerServiceCaseStudy";
import { DevOpsTransformationCaseStudy } from "./components/case-studies/DevOpsTransformationCaseStudy";

// Blog Pages
import { AllArticlesPage } from "./pages/AllArticlesPage";
import { FutureOfAIPage } from "./components/blog/FutureOfAIPage";
import { CloudMigrationPage } from "./components/blog/CloudMigrationPage";
import { DevOpsCulturePage } from "./components/blog/DevOpsCulturePage";

// Shared Images
const heroImage = "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80";
const aiImage = "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80";
const infraImage = "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=600&q=80";
const cloudImage = "https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=600&q=80";
const partnerBanner = heroImage;
const partners = [
  { name: "Amazon Web Services", logo: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png", url: "https://aws.amazon.com" },
  { name: "Microsoft Azure", logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Microsoft_Azure_Logo.svg", url: "https://azure.microsoft.com" },
  { name: "Google Cloud", logo: "https://cloud.google.com/_static/cloud/images/social-icon-google-cloud-1200-630.png", url: "https://cloud.google.com" },
  { name: "HashiCorp", logo: "https://www.hashicorp.com/themes/custom/hashicorp/logo.svg", url: "https://www.hashicorp.com" }
];

function SEO({ title, description, keywords }) {
  return (
    <Helmet>
      <title>{title} | KOBRALABS</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  keywords: PropTypes.string.isRequired,
};

function NavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <nav className="bg-gray-950 dark:bg-gray-900 text-white px-6 py-4 flex justify-between items-center shadow sticky top-0 z-50">
      <Link to="/" className="flex items-center space-x-3 hover:text-blue-400 transition">
        <img src="/logo.png" alt="KOBRALABS Logo" className="h-8 w-auto" />
        <span className="text-2xl font-bold tracking-widest">KOBRALABS</span>
      </Link>
      
      {/* Desktop Menu */}
      <div className="hidden md:flex items-center space-x-6">
        <Link to="/ai" className="hover:text-blue-400 transition">AI & Automation</Link>
        <Link to="/infrastructure" className="hover:text-blue-400 transition">Infrastructure</Link>
        <Link to="/cloud" className="hover:text-blue-400 transition">Cloud & DevOps</Link>
        <Link to="/about" className="hover:text-blue-400 transition">About</Link>
        <Link to="/contact" className="hover:text-blue-400 transition">Contact</Link>
        <ThemeToggle />
      </div>

      {/* Mobile Menu Button */}
      <div className="flex items-center space-x-4 md:hidden">
        <ThemeToggle />
        <button 
          className="text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 right-0 bg-gray-900 dark:bg-gray-800 md:hidden"
          >
            <div className="flex flex-col p-4 space-y-4">
              <Link 
                to="/ai" 
                className="hover:text-blue-400 transition"
                onClick={() => setIsMenuOpen(false)}
              >
                AI & Automation
              </Link>
              <Link 
                to="/infrastructure" 
                className="hover:text-blue-400 transition"
                onClick={() => setIsMenuOpen(false)}
              >
                Infrastructure
              </Link>
              <Link 
                to="/cloud" 
                className="hover:text-blue-400 transition"
                onClick={() => setIsMenuOpen(false)}
              >
                Cloud & DevOps
              </Link>
              <Link 
                to="/about" 
                className="hover:text-blue-400 transition"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="hover:text-blue-400 transition"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-gray-950 text-gray-400 text-center py-6 mt-16 border-t border-gray-800">
      <div>KOBRALABS &copy; {new Date().getFullYear()} &mdash; All rights reserved.</div>
      <div className="mt-2 text-xs">Empowering innovation with AI, Cloud, and Infrastructure.</div>
    </footer>
  );
}

function HomePage() {
  return (
    <main className="flex-1">
      <SEO
        title="Home"
        description="KOBRALABS - We blend advanced AI intelligence with resilient IT infrastructures to deliver custom solutions that drive growth and innovation."
        keywords="AI, automation, infrastructure, cloud, DevOps, IT solutions, technology consulting"
      />
      {/* Hero Section */}
      <section className="relative flex flex-col items-center justify-center py-24 px-4 text-center overflow-hidden bg-gray-900 dark:bg-gray-950">
        <motion.img
          src={heroImage}
          alt="Abstract digital network background"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 1.5 }}
        />
        <motion.h1
          className="relative text-5xl md:text-7xl font-bold text-white drop-shadow-xl"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        >
          KOBRALABS
        </motion.h1>
        <motion.p
          className="relative mt-4 text-lg md:text-xl max-w-2xl text-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          We blend advanced AI intelligence with resilient IT infrastructures to deliver custom solutions that drive growth and innovation.
        </motion.p>
      </section>
      {/* Services Cards */}
      <section id="services" className="py-20 px-6 bg-gray-950 dark:bg-gray-900 text-center">
        <h2 className="text-4xl font-bold text-white mb-12">Our Expertise</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <Link to="/ai" className="block">
            <motion.div
              className="bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              whileHover={{ scale: 1.05 }}
            >
              <img src={aiImage} alt="AI & Automation" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-2xl font-semibold text-white mb-2">AI & Automation</h3>
                <p className="text-gray-400">
                  From predictive analytics and natural language processing to end-to-end automation,
                  we empower you with intelligent systems that learn, adapt, and optimize your business processes.
                </p>
              </div>
            </motion.div>
          </Link>
          <Link to="/infrastructure" className="block">
            <motion.div
              className="bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              whileHover={{ scale: 1.05 }}
            >
              <img src={infraImage} alt="Infrastructure & Networking" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-2xl font-semibold text-white mb-2">Infrastructure & Networking</h3>
                <p className="text-gray-400">
                  Architect robust, high-performance networks and data centers tailored for scalability,
                  security, and seamless operations—ensuring uptime and resilience for your critical applications.
                </p>
              </div>
            </motion.div>
          </Link>
          <Link to="/cloud" className="block">
            <motion.div
              className="bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              whileHover={{ scale: 1.05 }}
            >
              <img src={cloudImage} alt="Cloud & DevOps" className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-2xl font-semibold text-white mb-2">Cloud & DevOps</h3>
                <p className="text-gray-400">
                  We deliver end-to-end cloud transformations, leveraging Infrastructure-as-Code,
                  CI/CD pipelines, and proactive monitoring to accelerate delivery and maintain peak reliability.
                </p>
              </div>
            </motion.div>
          </Link>
        </div>
      </section>
      {/* Case Studies Section */}
      <CaseStudiesSection />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Blog Section */}
      <BlogSection />

      {/* Newsletter Section */}
      <NewsletterSection />

      {/* Partners Section */}
      <section id="partners" className="relative py-20 px-6 text-center overflow-hidden bg-gray-900 dark:bg-gray-950">
        <motion.img
          src={partnerBanner}
          alt="Partners Banner"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1.5 }}
        />
        <h2 className="relative text-4xl font-bold text-white mb-12">Our Partners</h2>
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto items-center">
          {partners.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 p-6 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-all"
            >
              <img src={p.logo} alt={p.name} className="max-h-12 object-contain" />
            </a>
          ))}
        </div>
      </section>
    </main>
  );
}

function ServiceCard({ title, description, icon, features }) {
  return (
    <motion.div
      className="bg-gray-800 rounded-lg p-6"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className="text-blue-400 mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-gray-400 mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center text-gray-300">
            <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

ServiceCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function AiPage() {
  const services = [
    {
      title: "Machine Learning Solutions",
      description: "Custom ML models and algorithms to solve complex business problems",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      features: [
        "Predictive Analytics",
        "Natural Language Processing",
        "Computer Vision",
        "Recommendation Systems"
      ]
    },
    {
      title: "Process Automation",
      description: "Intelligent automation solutions to streamline operations",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      features: [
        "RPA Implementation",
        "Workflow Automation",
        "Document Processing",
        "Business Process Optimization"
      ]
    },
    {
      title: "AI Strategy & Consulting",
      description: "Expert guidance on implementing AI in your organization",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      features: [
        "AI Readiness Assessment",
        "Technology Roadmap",
        "Team Training",
        "ROI Analysis"
      ]
    }
  ];

  return (
    <section className="py-24 px-4 bg-gray-900 min-h-[60vh]">
      <SEO
        title="AI & Automation"
        description="Leverage artificial intelligence and automation to drive innovation, improve efficiency, and gain competitive advantage."
        keywords="AI, machine learning, automation, RPA, predictive analytics, NLP, computer vision"
      />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-6">AI & Automation</h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            We help organizations leverage artificial intelligence and automation to drive innovation,
            improve efficiency, and gain competitive advantage.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
}

function InfraPage() {
  const services = [
    {
      title: "Network Infrastructure",
      description: "Design and implementation of robust network solutions",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      features: [
        "Network Design",
        "Security Implementation",
        "Performance Optimization",
        "Disaster Recovery"
      ]
    },
    {
      title: "Data Center Solutions",
      description: "Modern data center infrastructure and management",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      features: [
        "Infrastructure Design",
        "Virtualization",
        "Storage Solutions",
        "Power Management"
      ]
    },
    {
      title: "Security & Compliance",
      description: "Comprehensive security solutions and compliance management",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      features: [
        "Security Assessment",
        "Compliance Auditing",
        "Access Control",
        "Threat Management"
      ]
    }
  ];

  return (
    <section className="py-24 px-4 bg-gray-900 min-h-[60vh]">
      <SEO
        title="Infrastructure & Networking"
        description="Design, implement, and manage robust IT infrastructure solutions that form the backbone of your organization&apos;s technology ecosystem."
        keywords="infrastructure, networking, data center, security, compliance, IT infrastructure"
      />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-6">Infrastructure & Networking</h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            We design, implement, and manage robust IT infrastructure solutions that form the backbone
            of your organization&apos;s technology ecosystem.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CloudPage() {
  const services = [
    {
      title: "Cloud Migration",
      description: "Seamless transition to cloud infrastructure",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      features: [
        "Migration Strategy",
        "Application Modernization",
        "Data Migration",
        "Performance Optimization"
      ]
    },
    {
      title: "DevOps Implementation",
      description: "Streamline development and operations",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      features: [
        "CI/CD Pipelines",
        "Infrastructure as Code",
        "Container Orchestration",
        "Monitoring & Logging"
      ]
    },
    {
      title: "Cloud Management",
      description: "Comprehensive cloud infrastructure management",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      features: [
        "Cost Optimization",
        "Security Management",
        "Performance Monitoring",
        "Disaster Recovery"
      ]
    }
  ];

  return (
    <section className="py-24 px-4 bg-gray-900 min-h-[60vh]">
      <SEO
        title="Cloud & DevOps"
        description="Leverage cloud technologies and DevOps practices to achieve greater agility, scalability, and operational efficiency."
        keywords="cloud, DevOps, cloud migration, CI/CD, infrastructure as code, container orchestration"
      />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-6">Cloud & DevOps</h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            We help organizations leverage cloud technologies and DevOps practices to achieve
            greater agility, scalability, and operational efficiency.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutPage() {
  return (
    <section className="py-24 px-4 bg-gray-900 min-h-[60vh]">
      <SEO
        title="About Us"
        description="Learn about KOBRALABS - our mission, values, and commitment to delivering innovative technology solutions."
        keywords="about KOBRALABS, company mission, technology values, IT consulting"
      />
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8 text-center">About KOBRALABS</h1>
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 mb-6">
            At KOBRALABS, we&apos;re passionate about transforming businesses through cutting-edge technology solutions. 
            Our team of experts combines deep technical knowledge with industry best practices to deliver 
            innovative solutions that drive growth and efficiency.
          </p>
          <h2 className="text-2xl font-semibold text-white mt-8 mb-4">Our Mission</h2>
          <p className="text-gray-300 mb-6">
            To empower organizations with intelligent, scalable, and secure technology solutions that enable 
            them to thrive in the digital age. We believe in building lasting partnerships with our clients 
            and delivering value through innovation and excellence.
          </p>
          <h2 className="text-2xl font-semibold text-white mt-8 mb-4">Our Values</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Innovation and Excellence</li>
            <li>Client-Centric Approach</li>
            <li>Technical Expertise</li>
            <li>Continuous Learning</li>
            <li>Integrity and Trust</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function ContactPage() {
  return (
    <section className="py-24 px-4 bg-gray-900 dark:bg-gray-950 min-h-[60vh]">
      <SEO
        title="Contact Us"
        description="Get in touch with KOBRALABS to discuss your technology needs and discover how we can help transform your business."
        keywords="contact KOBRALABS, get in touch, technology consultation, project inquiry"
      />
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-8 text-center">Contact Us</h1>
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-semibold text-white mb-4">Get in Touch</h2>
            <p className="text-gray-300 mb-6">
              Have a project in mind or want to learn more about our services? 
              We&apos;d love to hear from you. Fill out the form and we&apos;ll get back to you as soon as possible.
            </p>
            <div className="space-y-4">
              <div className="flex items-center text-gray-300">
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>info@kobralabs.xyz</span>
              </div>
              <div className="flex items-center text-gray-300">
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>+61400178091</span>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <div className="flex flex-col min-h-screen bg-gray-950 dark:bg-gray-900">
          <NavBar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/ai" element={<AiPage />} />
            <Route path="/infrastructure" element={<InfraPage />} />
            <Route path="/cloud" element={<CloudPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/case-studies/cloud-migration" element={<CloudMigrationCaseStudy />} />
            <Route path="/case-studies/ai-customer-service" element={<AICustomerServiceCaseStudy />} />
            <Route path="/case-studies/devops-transformation" element={<DevOpsTransformationCaseStudy />} />
            <Route path="/blog" element={<AllArticlesPage />} />
            <Route path="/blog/future-of-ai" element={<FutureOfAIPage />} />
            <Route path="/blog/cloud-migration" element={<CloudMigrationPage />} />
            <Route path="/blog/devops-culture" element={<DevOpsCulturePage />} />
          </Routes>
          <Footer />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
